/**
 * Redux Slice for Revision State
 *
 * Manages revision state including current revision, comparison state,
 * and UI preferences for the revision system.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { RevisionComparison, RevisionSettings } from '../types';

export interface RevisionState {
  /** Currently selected revision for viewing */
  currentRevisionId: string | null;
  /** Currently selected file for revision history */
  currentFileId: string | null;
  /** Revision comparison state */
  comparison: {
    /** Whether comparison mode is active */
    isActive: boolean;
    /** Left (older) revision ID */
    leftRevisionId: string | null;
    /** Right (newer) revision ID */
    rightRevisionId: string | null;
    /** Cached comparison result */
    comparisonResult: RevisionComparison | null;
  };
  /** UI preferences */
  ui: {
    /** Show revision indicators in file list */
    showRevisionIndicators: boolean;
    /** Show sync status for account users */
    showSyncStatus: boolean;
    /** Revision history panel visibility */
    historyPanelOpen: boolean;
    /** Diff viewer layout: side-by-side or inline */
    diffViewerLayout: 'side-by-side' | 'inline';
    /** Number of revisions to show in history */
    historyLimit: number;
  };
  /** Sync state for account users */
  sync: {
    /** Whether background sync is active */
    isSyncing: boolean;
    /** Last sync timestamp */
    lastSyncTime: string | null;
    /** Number of pending revisions */
    pendingCount: number;
    /** Sync errors */
    syncErrors: string[];
  };
  /** Local settings override */
  settings: Partial<RevisionSettings> | null;
}

const initialState: RevisionState = {
  currentRevisionId: null,
  currentFileId: null,
  comparison: {
    isActive: false,
    leftRevisionId: null,
    rightRevisionId: null,
    comparisonResult: null,
  },
  ui: {
    showRevisionIndicators: true,
    showSyncStatus: false,
    historyPanelOpen: false,
    diffViewerLayout: 'side-by-side',
    historyLimit: 50,
  },
  sync: {
    isSyncing: false,
    lastSyncTime: null,
    pendingCount: 0,
    syncErrors: [],
  },
  settings: null,
};

const revisionSlice = createSlice({
  name: 'revisions',
  initialState,
  reducers: {
    // Set current revision for viewing
    setCurrentRevision: (state, action: PayloadAction<string | null>) => {
      state.currentRevisionId = action.payload;
    },

    // Set current file for revision history
    setCurrentFile: (state, action: PayloadAction<string | null>) => {
      state.currentFileId = action.payload;
      // Reset comparison when switching files
      if (state.comparison.isActive) {
        state.comparison.isActive = false;
        state.comparison.leftRevisionId = null;
        state.comparison.rightRevisionId = null;
        state.comparison.comparisonResult = null;
      }
    },

    // Start revision comparison
    startComparison: (
      state,
      action: PayloadAction<{ leftRevisionId: string; rightRevisionId: string }>
    ) => {
      state.comparison.isActive = true;
      state.comparison.leftRevisionId = action.payload.leftRevisionId;
      state.comparison.rightRevisionId = action.payload.rightRevisionId;
      state.comparison.comparisonResult = null;
    },

    // Update comparison result
    setComparisonResult: (
      state,
      action: PayloadAction<RevisionComparison | null>
    ) => {
      state.comparison.comparisonResult = action.payload;
    },

    // End comparison mode
    endComparison: (state) => {
      state.comparison.isActive = false;
      state.comparison.leftRevisionId = null;
      state.comparison.rightRevisionId = null;
      state.comparison.comparisonResult = null;
    },

    // Toggle history panel
    toggleHistoryPanel: (state) => {
      state.ui.historyPanelOpen = !state.ui.historyPanelOpen;
    },

    // Set history panel visibility
    setHistoryPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.historyPanelOpen = action.payload;
    },

    // Toggle revision indicators
    toggleRevisionIndicators: (state) => {
      state.ui.showRevisionIndicators = !state.ui.showRevisionIndicators;
    },

    // Toggle sync status display
    toggleSyncStatus: (state) => {
      state.ui.showSyncStatus = !state.ui.showSyncStatus;
    },

    // Set diff viewer layout
    setDiffViewerLayout: (
      state,
      action: PayloadAction<'side-by-side' | 'inline'>
    ) => {
      state.ui.diffViewerLayout = action.payload;
    },

    // Set history limit
    setHistoryLimit: (state, action: PayloadAction<number>) => {
      state.ui.historyLimit = action.payload;
    },

    // Update sync state
    setSyncState: (
      state,
      action: PayloadAction<{
        isSyncing?: boolean;
        lastSyncTime?: string | null;
        pendingCount?: number;
      }>
    ) => {
      if (action.payload.isSyncing !== undefined) {
        state.sync.isSyncing = action.payload.isSyncing;
      }
      if (action.payload.lastSyncTime !== undefined) {
        state.sync.lastSyncTime = action.payload.lastSyncTime;
      }
      if (action.payload.pendingCount !== undefined) {
        state.sync.pendingCount = action.payload.pendingCount;
      }
    },

    // Add sync error
    addSyncError: (state, action: PayloadAction<string>) => {
      if (!state.sync.syncErrors.includes(action.payload)) {
        state.sync.syncErrors.push(action.payload);
      }
    },

    // Clear sync errors
    clearSyncErrors: (state) => {
      state.sync.syncErrors = [];
    },

    // Update local settings
    updateLocalSettings: (
      state,
      action: PayloadAction<Partial<RevisionSettings>>
    ) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },

    // Reset revision state
    resetRevisionState: () => initialState,
  },
});

// Export actions
export const {
  setCurrentRevision,
  setCurrentFile,
  startComparison,
  setComparisonResult,
  endComparison,
  toggleHistoryPanel,
  setHistoryPanelOpen,
  toggleRevisionIndicators,
  toggleSyncStatus,
  setDiffViewerLayout,
  setHistoryLimit,
  setSyncState,
  addSyncError,
  clearSyncErrors,
  updateLocalSettings,
  resetRevisionState,
} = revisionSlice.actions;

// Selectors
export const selectCurrentRevision = (state: RootState) =>
  state.revisions.currentRevisionId;

export const selectCurrentFile = (state: RootState) =>
  state.revisions.currentFileId;

export const selectComparison = (state: RootState) =>
  state.revisions.comparison;

export const selectRevisionUI = (state: RootState) => state.revisions.ui;

export const selectSyncState = (state: RootState) => state.revisions.sync;

export const selectLocalSettings = (state: RootState) =>
  state.revisions.settings;

export const selectIsComparing = (state: RootState) =>
  state.revisions.comparison.isActive;

export const selectHistoryPanelOpen = (state: RootState) =>
  state.revisions.ui.historyPanelOpen;

export const selectShowRevisionIndicators = (state: RootState) =>
  state.revisions.ui.showRevisionIndicators;

export const selectShowSyncStatus = (state: RootState) =>
  state.revisions.ui.showSyncStatus;

export const selectDiffViewerLayout = (state: RootState) =>
  state.revisions.ui.diffViewerLayout;

export const selectIsSyncing = (state: RootState) =>
  state.revisions.sync.isSyncing;

export const selectPendingSyncCount = (state: RootState) =>
  state.revisions.sync.pendingCount;

export const selectSyncErrors = (state: RootState) =>
  state.revisions.sync.syncErrors;

export default revisionSlice.reducer;
